import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Mail, LogIn, PlayCircle, TrendingUp, QrCode, Gift, Phone, ArrowRight, CheckCircle2 } from "lucide-react";
import { PUBLIC_HOST } from "../lib/domain";

const STEPS = [
  { n: 1, icon: Mail, title: "Reçois ton accès", text: "Après ton paiement, tu reçois un courriel de bienvenue. Garde-le : il contient ton lien d'accès." },
  { n: 2, icon: LogIn, title: "Connecte-toi (sans mot de passe)", text: `Va sur ${PUBLIC_HOST}/membres, entre ton courriel → tu reçois un lien magique. Clique-le, et te voilà connecté !` },
  { n: 3, icon: PlayCircle, title: "Accède au curriculum", text: "Dans ton espace membre, retrouve les vidéos techniques et le curriculum complet (Kickboxing, Muay Thaï, Jiu-Jitsu)." },
  { n: 4, icon: TrendingUp, title: "Suis ta progression", text: "Ton carnet Kaizen suit tes présences, tes techniques et ton avancée vers la prochaine ceinture." },
];

const PERKS = [
  { icon: QrCode, title: "Check-in rapide", text: "Scanne ton code à chaque cours pour enregistrer ta présence." },
  { icon: Gift, title: "Parrainage 10 $ / 10 $", text: "Invite un ami : il reçoit 10 $ de rabais, tu reçois 10 $ de crédit." },
  { icon: TrendingUp, title: "Progression ceintures", text: "Visualise ton chemin et ce qu'il te reste pour passer au niveau suivant." },
];

export default function NouveauMembreGuide() {
  useEffect(() => { document.title = "Bienvenue — Guide du nouveau membre"; }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-zinc-50" data-testid="nouveau-membre-guide">
      {/* Hero */}
      <section className="border-b border-zinc-800 bg-gradient-to-b from-red-950/30 to-[#0a0a0a]">
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-red-400 mb-4">Institut Impact Élite Martial · Kaizen</p>
          <h1 className="text-4xl sm:text-5xl font-black leading-tight">Bienvenue dans ta nouvelle plateforme 🥋</h1>
          <p className="mt-5 text-base md:text-lg text-zinc-300 leading-relaxed">
            Tout ce dont tu as besoin pour démarrer en 4 étapes simples. Aucun mot de passe à retenir.
          </p>
          <Link to="/membres" data-testid="guide-login-btn"
            className="mt-8 inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white font-semibold rounded-full px-7 py-4 text-sm uppercase tracking-wider transition-colors">
            <LogIn size={18} /> Me connecter à mon espace
          </Link>
        </div>
      </section>

      {/* Steps */}
      <section className="max-w-4xl mx-auto px-6 py-14">
        <h2 className="text-lg md:text-2xl font-bold mb-8">Démarrer en 4 étapes</h2>
        <div className="space-y-4">
          {STEPS.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.n} className="flex items-start gap-4 rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5" data-testid={`guide-step-${s.n}`}>
                <div className="shrink-0 w-11 h-11 rounded-full bg-red-600/15 border border-red-700/50 flex items-center justify-center">
                  <Icon size={20} className="text-red-400" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-red-500">ÉTAPE {s.n}</span>
                    <h3 className="font-bold text-lg">{s.title}</h3>
                  </div>
                  <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{s.text}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Perks */}
      <section className="border-t border-zinc-800 bg-zinc-950/50">
        <div className="max-w-4xl mx-auto px-6 py-14">
          <h2 className="text-lg md:text-2xl font-bold mb-8">Ce qui t'attend dans ton espace</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {PERKS.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5">
                  <Icon size={24} className="text-[#c9a961]" />
                  <h3 className="font-bold mt-3">{p.title}</h3>
                  <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{p.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Help */}
      <section className="max-w-3xl mx-auto px-6 py-14 text-center">
        <h2 className="text-2xl font-black">Besoin d'aide ?</h2>
        <p className="mt-3 text-zinc-300">On est là pour toi. Si tu ne reçois pas ton courriel d'accès ou si tu as une question, contacte-nous.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-x-8 gap-y-2 text-sm text-zinc-400">
          <a href="tel:+18195279446" className="inline-flex items-center gap-2 hover:text-white"><Phone size={15} /> (819) 527-9446</a>
          <a href="mailto:info@kaizenfranchise.com" className="inline-flex items-center gap-2 hover:text-white"><Mail size={15} /> info@kaizenfranchise.com</a>
        </div>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/membres" className="inline-flex items-center gap-2 bg-white text-black hover:bg-zinc-200 font-bold rounded-full px-6 py-3 text-sm uppercase tracking-wider transition-colors">
            Mon espace membre <ArrowRight size={16} />
          </Link>
          <Link to="/ete-2026" className="inline-flex items-center gap-2 border border-zinc-700 hover:border-zinc-500 text-zinc-200 rounded-full px-6 py-3 text-sm uppercase tracking-wider transition-colors">
            Voir les programmes
          </Link>
        </div>
        <p className="mt-10 inline-flex items-center gap-2 text-xs text-zinc-500">
          <CheckCircle2 size={14} className="text-emerald-500" /> Paiement sécurisé · Données protégées
        </p>
      </section>
    </div>
  );
}
